package dev.akshaychordiya.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.akshaychordiya.HeadlineTextStyle
import dev.akshaychordiya.MetaTextStyle
import dev.akshaychordiya.SectionTitleStyle
import dev.akshaychordiya.SubheadlineTextStyle
import dev.akshaychordiya.components.layouts.PageLayoutData
import dev.akshaychordiya.components.layouts.formatDate
import dev.akshaychordiya.components.widgets.ListRowStyle
import dev.akshaychordiya.content.Mentoring
import dev.akshaychordiya.content.Site
import dev.akshaychordiya.content.books
import dev.akshaychordiya.content.talks
import dev.akshaychordiya.fadeIn
import dev.akshaychordiya.fadeUp
import dev.akshaychordiya.generated.articleIndex
import dev.akshaychordiya.smoothTransition
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    // Empty title => the tab just reads "Akshay Chordiya" rather than "Home · Akshay Chordiya".
    ctx.data.add(PageLayoutData(""))
}

/** Stacks on a phone, sits side by side from tablet up. */
val HeroStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(1.75.cssRem)
            .margin(top = 1.cssRem)
            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
            .flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
    }
    Breakpoint.MD {
        Modifier
            .flexDirection(org.jetbrains.compose.web.css.FlexDirection.Row)
            .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
            .gap(2.5.cssRem)
    }
}

/**
 * Layout box for the portrait; [AvatarStyle] supplies the circular crop.
 *
 * These two have to sit on *separate* elements - a wrapper and the `<img>` inside it. Merged onto one element,
 * [AvatarStyle]'s `fillMaxSize` resolves against the hero container rather than this box, so `width: 100%` beats the
 * clamp below and the photo swells to the full column width.
 */
val AvatarBoxStyle = CssStyle.base {
    Modifier
        .size(clamp(7.cssRem, 24.vw, 10.cssRem))
        .flexShrink(0)
        .display(org.jetbrains.compose.web.css.DisplayStyle.Block)
}

val AvatarStyle = CssStyle.base {
    Modifier
        .fillMaxSize()
        // Circular, which also crops the conference logo out of the photo's top corner.
        .borderRadius(50.percent)
        .objectFit(ObjectFit.Cover)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
}

val SectionLinkStyle = CssStyle {
    base {
        Modifier
            .fontSize(0.9.cssRem)
            .fontWeight(500)
            .color(colorMode.toSitePalette().brandPrimary)
            .smoothTransition("opacity")
    }
    hover { Modifier.opacity(0.75) }
}

/**
 * The 1:1 call to action.
 *
 * Deliberately *not* the standard card treatment. The neutral surface sits only a few percent off the page
 * background, so a plain card reads as a washed-out grey box rather than a highlight - especially in light mode.
 * Tinting with the brand colour at low alpha keeps it unmistakably a panel while staying quiet enough not to shout
 * over the lists above it, and it derives from the palette so it follows the theme automatically.
 */
val CalloutStyle = CssStyle.base {
    val brand = colorMode.toSitePalette().brandPrimary.toRgb()
    Modifier
        .fillMaxWidth()
        .padding(1.75.cssRem)
        .borderRadius(0.9.cssRem)
        .backgroundColor(brand.copyf(alpha = if (colorMode.isLight) 0.07f else 0.10f))
        .border(1.px, LineStyle.Solid, brand.copyf(alpha = 0.30f))
        .gap(0.75.cssRem)
}

/** The action inside [CalloutStyle] - filled, because it is the one thing on the page worth clicking. */
val CalloutButtonStyle = CssStyle {
    base {
        Modifier
            .margin(top = 0.5.cssRem)
            .padding(topBottom = 0.6.cssRem, leftRight = 1.2.cssRem)
            .borderRadius(0.5.cssRem)
            .fontSize(0.95.cssRem)
            .fontWeight(600)
            .backgroundColor(colorMode.toSitePalette().brandPrimary)
            .color(colorMode.toSitePalette().background)
            .alignSelf(org.jetbrains.compose.web.css.AlignSelf.FlexStart)
            .smoothTransition("transform", "box-shadow")
    }
    hover {
        Modifier
            .translateY((-2).px)
            .boxShadow(
                offsetY = 6.px,
                blurRadius = 16.px,
                color = colorMode.toSitePalette().brandPrimary.toRgb().copyf(alpha = 0.35f),
            )
    }
}

@Composable
private fun SectionHeading(label: String, linkPath: String, linkText: String, index: Int) {
    Row(
        Modifier.fillMaxWidth().margin(top = 3.cssRem, bottom = 0.5.cssRem).fadeIn(index),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Div(SectionTitleStyle.toAttrs()) { SpanText(label) }
        com.varabyte.kobweb.compose.foundation.layout.Spacer()
        Link(
            linkPath,
            linkText,
            SectionLinkStyle.toModifier(),
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        )
    }
}

@Composable
private fun CompactRow(meta: String, title: String, path: String, external: Boolean = false, index: Int) {
    Div(ListRowStyle.toModifier().fadeUp(index).toAttrs()) {
        Link(path, variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)) {
            Column(Modifier.fillMaxWidth().gap(0.3.cssRem)) {
                SpanText(meta, MetaTextStyle.toModifier())
                SpanText(title, ArticleTitleStyle.toModifier())
            }
        }
    }
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    val palette = ColorMode.current.toSitePalette()

    Div(HeroStyle.toAttrs()) {
        Div(AvatarBoxStyle.toModifier().fadeIn().toAttrs()) {
            Img(
                src = "/images/akshay.webp",
                alt = "Akshay Chordiya speaking at Android Makers by droidcon",
                attrs = AvatarStyle.toModifier().toAttrs(),
            )
        }

        Column(Modifier.gap(1.cssRem)) {
            Div(HeadlineTextStyle.toModifier().fadeUp(index = 1).toAttrs()) {
                SpanText("Android, Kotlin, and the craft of explaining them.")
            }
            Div(SubheadlineTextStyle.toModifier().fadeUp(index = 2).toAttrs()) {
                SpanText(Site.CREDENTIAL)
                Br()
                SpanText(Site.LOCATION)
            }
        }
    }

    // Recent talks -------------------------------------------------------
    SectionHeading("Recent talks", "/talks", "All ${talks.size} talks →", index = 3)
    talks.take(3).forEachIndexed { i, talk ->
        CompactRow(
            meta = "${talk.event} · ${talk.date}",
            title = talk.title,
            path = "/talks",
            index = 4 + i,
        )
    }

    // Latest writing -----------------------------------------------------
    SectionHeading("Latest writing", "/articles", "All articles →", index = 0)
    articleIndex.take(3).forEachIndexed { i, article ->
        CompactRow(
            meta = formatDate(article.date),
            title = article.title,
            path = article.route,
            index = i,
        )
    }

    // Books --------------------------------------------------------------
    SectionHeading("Books", "/books", "Both books →", index = 0)
    books.forEachIndexed { i, book ->
        CompactRow(
            meta = if (book.inProgress) book.year else "${book.year} · ${book.publisher}",
            title = book.title,
            path = "/books",
            index = i,
        )
    }

    // 1:1 ----------------------------------------------------------------
    Column(CalloutStyle.toModifier().margin(top = 3.cssRem).fadeUp()) {
        SpanText(
            "Want to talk it through?",
            HeadlineTextStyle.toModifier().fontSize(1.35.cssRem).letterSpacing((-0.02).cssRem),
        )
        SpanText(
            "I do 1:1 sessions on career, Android and Kotlin, public speaking, and moving abroad as an engineer.",
            Modifier.fontSize(1.cssRem).color(palette.subdued),
        )
        Link(
            path = Mentoring.TOPMATE_PROFILE,
            modifier = CalloutButtonStyle.toModifier(),
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        ) {
            SpanText("See the sessions →")
        }
    }
}
